import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';

import { useFeatureGate } from '../hooks/useFeatureGates';
import { useTransitionedPresence, type ITransitionedPresence } from '../hooks/useTransitionedPresence';

/** Which surface occupies the side panel. The two are mutually exclusive — the column seats one. */
export type SidePanelContent = 'assistant' | 'alerts';

/** One duration for every panel motion: the column's width collapse/expand and the Alerts Center sheet. */
export const SIDE_PANEL_TRANSITION_MS = 500;

/**
 * The viewport breakpoint at which `EntityPaneBody` switches from one to two
 * columns. Shifts to `2xl` while the side panel occupies horizontal space so
 * the narrowed content track does not try to host two columns until there is
 * genuinely room.
 */
export type ContentColumnsBreakpoint = 'xl' | '2xl';

/** An alert handed to the assistant thread, waiting for the pane to open it. */
export interface IPendingAlertExpand {
  alertId: string;
  /**
   * Whether the row was still unread when it was clicked. Carried rather than
   * re-read later because the same click marks the alert read, so by the time
   * the reply renders the stored status no longer answers "was this new to the
   * user" — see `AssistantPaneView`.
   */
  wasUnread: boolean;
}

/** The panel's own state, before the gates decide whether it can be shown. */
interface ISidePanelState {
  isOpen: boolean;
  content: SidePanelContent;
}

/** Which gates the viewer passes, for {@link deriveVisiblePanel}. */
interface ISidePanelGates {
  isAssistantEnabled: boolean;
  isAlertsEnabled: boolean;
}

interface ISidePanelContextValue extends ISidePanelState, ISidePanelGates {
  /**
   * What the panel would render, or `null` when nothing is showable. `HomeScene`
   * still owns the `xl+` check — below that breakpoint there is no column.
   */
  visible: SidePanelContent | null;
  /**
   * The `xl+` column's presentational presence: `renderValue` retains the
   * occupant through the ~{@link SIDE_PANEL_TRANSITION_MS} exit so `HomeScene`
   * can play the collapse before unmounting, and `stage` names where the width
   * transition is. Lives here rather than in the scene because
   * {@link ContentColumnsBreakpoint} must flip with the column the user *sees*:
   * keying it on `visible` re-lays panes out at animation start, a visible
   * one-frame snap before the width has moved.
   */
  panelPresence: ITransitionedPresence<SidePanelContent>;
  /** `2xl` while the panel consumes content width, else `xl`. */
  columnsBreakpoint: ContentColumnsBreakpoint;
  /** The nav pill: closed → open on the assistant; showing alerts → assistant; showing assistant → closed. */
  toggleAssistant: () => void;
  /** The bell at `xl+`, and an `/alerts` deep link once the route gate hands off. */
  showAlerts: () => void;
  /** The Alerts Center's X — collapses the column back to the assistant thread. */
  showAssistant: () => void;
  /**
   * The alert the assistant has been asked to open but has not opened yet, or
   * `null` when nothing is waiting. Lives here rather than in either surface
   * because the two never overlap: below `xl` the Alerts Center and the
   * assistant are separate routes, so the pane that has to act on this is
   * unmounted at the moment the click happens.
   */
  pendingAlertExpand: IPendingAlertExpand | null;
  /**
   * Hand an alert to the assistant thread: gives it the column and records
   * which alert to open. Below `xl` the caller still has to navigate — see
   * `useOpenAlertInAssistant`, which is what surfaces should call.
   */
  openAlertInAssistant: (alertId: string, wasUnread: boolean) => void;
  /** Clears the pending alert, once the pane has issued its expand. */
  consumePendingExpand: () => void;
}

/**
 * Default reflects "no side panel" — gates off, today's column behavior. Lets
 * `EntityPaneBody` (and its pane Storybook stories) consume
 * {@link useContentColumnsBreakpoint} without being wrapped in the provider.
 */
const DEFAULT_VALUE: ISidePanelContextValue = {
  isOpen: false,
  content: 'assistant',
  isAssistantEnabled: false,
  isAlertsEnabled: false,
  visible: null,
  panelPresence: { renderValue: null, stage: 'open', onTransitionEnd: () => {} },
  columnsBreakpoint: 'xl',
  toggleAssistant: () => {},
  showAlerts: () => {},
  showAssistant: () => {},
  pendingAlertExpand: null,
  openAlertInAssistant: () => {},
  consumePendingExpand: () => {},
};

const SidePanelContext = React.createContext<ISidePanelContextValue>(DEFAULT_VALUE);

/**
 * What the panel actually renders. A closed panel shows nothing, and so does one
 * whose selected surface the viewer is not gated into — which is why this folds
 * both gates rather than trusting `content` alone.
 */
export const deriveVisiblePanel = (
  { isOpen, content }: ISidePanelState,
  { isAssistantEnabled, isAlertsEnabled }: ISidePanelGates
): SidePanelContent | null => {
  if (!isOpen) {
    return null;
  }

  const enabled = content === 'alerts' ? isAlertsEnabled : isAssistantEnabled;
  return enabled ? content : null;
};

/**
 * `2xl` while the panel occupies content width, else `xl`. Keyed on whether
 * *something* is visible rather than on the assistant gate, so the column
 * reflows identically for either occupant.
 */
export const deriveContentColumnsBreakpoint = (hasVisiblePanel: boolean): ContentColumnsBreakpoint =>
  hasVisiblePanel ? '2xl' : 'xl';

/**
 * The nav pill's three-way toggle. It speaks for the assistant only, so from
 * the Alerts Center it swaps the column rather than closing it — pressing
 * "Mantle Assistant" and getting an empty column would read as a broken button.
 */
export const nextStateOnAssistantToggle = ({ isOpen, content }: ISidePanelState): ISidePanelState => {
  if (isOpen && content === 'assistant') {
    return { isOpen: false, content: 'assistant' };
  }

  return { isOpen: true, content: 'assistant' };
};

interface ISidePanelProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the cross-cutting state for the `xl+` side panel: which of its two
 * surfaces it shows (the assistant thread or the Alerts Center — the column
 * seats one at a time), whether each is available (`feat-assistant`,
 * `feat-alerts-center`), and whether it is open at all.
 *
 * It also carries the one hand-off between those surfaces: the alert an Alerts
 * Center row click asked the assistant to open. That cannot live in either
 * surface, because below `xl` they are separate routes and the pane that has to
 * act on it is not mounted when the click happens.
 *
 * State is in-memory and starts open on the assistant, so a gated viewer sees
 * today's behavior on load and the Alerts Center never opens itself. It resets
 * on reload (the app has no persisted-UI-state pattern).
 *
 * Consumed by `HomeScene` (renders the panel + reflows content),
 * `MantleAssistantNavButton` and `MainSidebar` (toggle it, reflect it), and
 * `MainApp`'s `AlertsRouteGate` (hands a deep link off to it) via
 * {@link useSidePanel}; and by `EntityPaneBody` via the narrower
 * {@link useContentColumnsBreakpoint}, so a generic layout primitive depends
 * only on "which breakpoint do my columns use".
 */
const SidePanelProvider = ({ children }: ISidePanelProviderProps) => {
  const isAssistantEnabled = useFeatureGate(FeatureGate.Assistant);
  const isAlertsEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const [state, setState] = React.useState<ISidePanelState>({ isOpen: true, content: 'assistant' });
  // Deliberately outside `ISidePanelState`: that shape is what the layout
  // derivations above are typed against, and an alert waiting to be opened
  // decides nothing about the column.
  const [pendingAlertExpand, setPendingAlertExpand] = React.useState<IPendingAlertExpand | null>(null);

  const toggleAssistant = React.useCallback(() => setState(nextStateOnAssistantToggle), []);
  const showAlerts = React.useCallback(() => setState({ isOpen: true, content: 'alerts' }), []);
  const showAssistant = React.useCallback(() => setState({ isOpen: true, content: 'assistant' }), []);

  const openAlertInAssistant = React.useCallback((alertId: string, wasUnread: boolean) => {
    setState({ isOpen: true, content: 'assistant' });
    setPendingAlertExpand({ alertId, wasUnread });
  }, []);

  const consumePendingExpand = React.useCallback(() => setPendingAlertExpand(null), []);

  const visible = deriveVisiblePanel(state, { isAssistantEnabled, isAlertsEnabled });

  // Presence tracks the column the user sees: `renderValue` retains the
  // occupant through the exit transition. Gated on `xl` here (not just in the
  // scene) so a downward crossing drops it instantly — the exit animation must
  // never hold `AssistantPane` mounted into the window where the `/assistant`
  // route could mount its WebSocket a second time.
  const isXl = useBreakpoint('xl');
  const panelPresence = useTransitionedPresence(isXl ? visible : null, {
    transitionProperty: 'width',
    durationMs: SIDE_PANEL_TRANSITION_MS,
  });
  const columnOccupiesWidth = isXl && panelPresence.renderValue !== null;

  const value = React.useMemo<ISidePanelContextValue>(() => {
    return {
      ...state,
      isAssistantEnabled,
      isAlertsEnabled,
      visible,
      panelPresence,
      // Keyed on presence, not `visible`: panes reflow when the column actually
      // leaves (or enters) the row, not at the logical toggle a half-second
      // before the width has finished (or started) moving.
      columnsBreakpoint: deriveContentColumnsBreakpoint(columnOccupiesWidth),
      toggleAssistant,
      showAlerts,
      showAssistant,
      pendingAlertExpand,
      openAlertInAssistant,
      consumePendingExpand,
    };
  }, [
    state,
    isAssistantEnabled,
    isAlertsEnabled,
    visible,
    panelPresence,
    columnOccupiesWidth,
    toggleAssistant,
    showAlerts,
    showAssistant,
    pendingAlertExpand,
    openAlertInAssistant,
    consumePendingExpand,
  ]);

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

export default SidePanelProvider;

/**
 * Reads the side-panel state. Used by the shell (`HomeScene`, to render and
 * place the panel), the nav toggle button, the bell, and the alerts route gate.
 * Outside the provider it returns the closed, gate-off default, so the no-op
 * value is safe in Storybook.
 *
 * @returns The full context value — `visible`, `isOpen`, `content`, both gate
 *   flags, `columnsBreakpoint`, the `toggleAssistant` / `showAlerts` /
 *   `showAssistant` setters, and the pending-expand hand-off
 *   (`pendingAlertExpand`, `openAlertInAssistant`, `consumePendingExpand`).
 */
export const useSidePanel = (): ISidePanelContextValue => React.useContext(SidePanelContext);

/**
 * Narrow read of just the column breakpoint, for `EntityPaneBody`. Layout-
 * neutral by name so a generic layout primitive depends on "which breakpoint
 * do my columns use," not on what happens to be in the panel.
 *
 * @returns `'2xl'` while the panel occupies content width, otherwise `'xl'`.
 */
export const useContentColumnsBreakpoint = (): ContentColumnsBreakpoint =>
  React.useContext(SidePanelContext).columnsBreakpoint;
